import { useState } from 'react';
import { ClickAwayListener } from '@mui/material';
import { noop } from 'lodash';
import { faChevronDown, faImage } from '@fortawesome/pro-solid-svg-icons';
import { clsx } from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { SearchInput } from '@design-system/components/searchinput';
import { Checkbox } from '@design-system/components/checkbox/checkbox';
import { Typography } from '@design-system/components/typography/typography';
import {
  dropDownWrapper,
  dropDownList,
  visible,
  rowLayout,
} from '@client/new/pages/bundles/otp/view/dropDownMultiselect/dropDownMultiselect.css';

// Match react-select's default control border / indicator color.
const SELECT_BORDER_COLOR = 'hsl(0, 0%, 80%)';

export type WorkoutOption = {
  value: number;
  label: string;
  programId: number | null;
  imgUrl?: string;
};

export const WorkoutThumbnail = ({
  imgUrl,
  alt,
  className,
}: {
  imgUrl?: string;
  alt: string;
  className: string;
}) => (
  <div
    className={clsx(
      className,
      'flex items-center justify-center overflow-hidden bg-[#E2E2E2]',
    )}
  >
    {imgUrl ? (
      <img src={imgUrl} alt={alt} className="w-full h-full object-cover" />
    ) : (
      <FontAwesomeIcon icon={faImage} className="text-[#9E9E9E]" />
    )}
  </div>
);

export type WorkoutOptionGroup = {
  label: string;
  options: WorkoutOption[];
};

type Props = {
  groups: WorkoutOptionGroup[];
  value: number[];
  onChange: (value: number[]) => void;
  disabled?: boolean;
};

const toggle = (id: number, selected: number[]) =>
  selected.includes(id) ? selected.filter((v) => v !== id) : [...selected, id];

export const WorkoutsDropDownMultiselect = ({
  groups,
  value,
  onChange,
  disabled,
}: Props) => {
  const [open, setOpen] = useState(false);
  const [searchText, setSearchText] = useState('');

  const search = searchText.toLocaleLowerCase();
  const filteredGroups = groups
    .map((group) => ({
      ...group,
      options: group.options.filter((o) =>
        o.label.toLocaleLowerCase().includes(search),
      ),
    }))
    .filter((group) => group.options.length > 0);

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <div className="relative">
        <div
          className="flex items-center justify-between"
          style={{
            border: `1px solid ${SELECT_BORDER_COLOR}`,
            borderRadius: 4,
            minHeight: 38,
            padding: '2px 8px',
            cursor: disabled ? 'default' : 'pointer',
          }}
          onClick={() => !disabled && setOpen((prev) => !prev)}
        >
          <Typography variant="body" size="large">
            Select
          </Typography>
          <div className="flex items-center gap-2">
            <span
              style={{
                width: 1,
                height: 20,
                backgroundColor: SELECT_BORDER_COLOR,
              }}
            />
            <FontAwesomeIcon
              icon={faChevronDown}
              style={{ color: SELECT_BORDER_COLOR, padding: '0 4px' }}
            />
          </div>
        </div>
        <div
          className={clsx(open && visible, dropDownWrapper)}
          style={{ width: '100%', maxWidth: '100%', position: 'relative' }}
        >
          <SearchInput
            placeholder="Search"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onClear={() => setSearchText('')}
            disabled={!!disabled}
          />
          <div className={dropDownList}>
            {filteredGroups.map((group) => (
              <div key={group.label}>
                <div className="px-2 pt-2">
                  <Typography variant="body" size="medium" color="N-80">
                    {group.label}
                  </Typography>
                </div>
                {group.options.map((option) => (
                  <div
                    key={option.value}
                    className={clsx('p-2', rowLayout)}
                    onClick={() => onChange(toggle(option.value, value || []))}
                  >
                    <div className="flex items-center gap-2">
                      <WorkoutThumbnail
                        imgUrl={option.imgUrl}
                        alt={option.label}
                        className="w-8 h-8 rounded"
                      />
                      <Checkbox
                        label={option.label}
                        onChange={noop}
                        checked={(value || []).includes(option.value)}
                        textVariant="body"
                        textSize="large"
                        checkboxVariant="neutral"
                      />
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    </ClickAwayListener>
  );
};
